import React, { useState, useRef } from 'react';
import { Box, Button, Typography, CircularProgress, Alert, Paper } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useHistory, useLocation } from 'react-router-dom';
import { UploadService } from '../../services/dao/uploader-service';

const uploaderService = new UploadService();
const VALID_TYPES = ['image/jpeg', 'image/png', 'application/pdf', 'image/tiff'];

const resizeImage = (file, maxSize) => new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = e => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            let w = img.width, h = img.height;
            if (w > h) { if (w > maxSize) { h *= maxSize / w; w = maxSize; } }
            else { if (h > maxSize) { w *= maxSize / h; h = maxSize; } }
            canvas.width = w; canvas.height = h;
            canvas.getContext('2d').drawImage(img, 0, 0, w, h);
            canvas.toBlob(blob => resolve(new File([blob], file.name, { type: file.type })), file.type, 0.7);
        };
        img.src = e.target.result;
    };
    reader.readAsDataURL(file);
});

export const UploadNewReceipt = () => {
    const history = useHistory();
    const location = useLocation();
    const projectId = new URLSearchParams(location.search).get('projectId');
    const fileRef = useRef(null);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleFile = (e) => {
        const f = e.target.files[0];
        if (!f) return;
        if (!VALID_TYPES.includes(f.type)) { setError('Only JPEG, PNG, PDF, and TIFF files are allowed.'); return; }
        setFile(f);
        setPreview(URL.createObjectURL(f));
        setError('');
        setSuccess(false);
    };

    const handleUpload = async () => {
        if (!file) return;
        setLoading(true); setError('');
        const toUpload = file.size > 9 * 1024 * 1024 ? await resizeImage(file, 2048) : file;
        const res = await uploaderService.uploadFile(toUpload, 'receipt', projectId);
        setLoading(false);
        if (res?.error) setError(res.errorMessage || 'Upload failed');
        else { setSuccess(true); setFile(null); setPreview(null); }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const f = e.dataTransfer.files[0];
        if (f) { handleFile({ target: { files: [f] } }); }
    };

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 4, maxWidth: 600, mx: 'auto' }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => history.goBack()}
                sx={{ mb: 3, color: 'text.secondary', fontWeight: 600, borderRadius: 2, textTransform: 'none' }}>
                Back
            </Button>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <Box sx={{ width: 44, height: 44, borderRadius: 2, background: '#FBF5EB',
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ReceiptLongIcon sx={{ color: '#C89E57', fontSize: '1.4rem' }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>Upload Receipt</Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2, borderRadius: 2 }}
                action={<Button size="small" onClick={() => setSuccess(false)}>Upload Another</Button>}>
                Receipt uploaded and processing!
            </Alert>}

            {!file && !success && (
                <Paper variant="outlined" sx={{ borderRadius: 3, border: '2px dashed rgba(0,0,0,0.12)', p: 6,
                    textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s',
                    '&:hover': { borderColor: '#C89E57', background: '#FBF5EB' } }}
                    onClick={() => fileRef.current?.click()}
                    onDragOver={e => e.preventDefault()} onDrop={handleDrop}>
                    <input ref={fileRef} type="file" hidden accept=".jpeg,.jpg,.png,.pdf,.tiff" onChange={handleFile} />
                    <CloudUploadIcon sx={{ fontSize: 48, color: '#C89E57', mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>Drop your receipt here</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>or click to browse · JPEG, PNG, PDF, TIFF</Typography>
                </Paper>
            )}

            {file && (
                <Box>
                    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', mb: 2 }}>
                        {preview && file.type.startsWith('image/') && (
                            <img src={preview} alt="Preview" style={{ width: '100%', maxHeight: 400, objectFit: 'contain', background: '#F8FAFC' }} />
                        )}
                        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{file.name}</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {(file.size / 1024).toFixed(0)} KB
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button variant="outlined" fullWidth onClick={() => { setFile(null); setPreview(null); }}
                            sx={{ borderRadius: 2, textTransform: 'none' }}>Cancel</Button>
                        <Button variant="contained" fullWidth onClick={handleUpload} disabled={loading}
                            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                            sx={{ borderRadius: 2, textTransform: 'none' }}>
                            {loading ? 'Uploading...' : 'Upload'}
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
};
